import { tierName } from '@h2h/shared';
import { TierDot } from '../DivisionStrip';
import { Placeholder } from '../../ui/Placeholder';
import { Users } from '../../ui/icons';
import { cap, initials, ordinalSuffix } from '../../../lib/format';
import { type FriendStanding } from '../../../hooks/useQueries';
import { useOpenProfile } from '../../../hooks/useOpenProfile';

export function FriendsStandingsView({
  rows,
  leagueName,
  onInvite
}: {
  rows: FriendStanding[];
  leagueName: string;
  onInvite?: () => void;
}) {
  const myIndex = rows.findIndex((r) => r.isYou);
  const myRank = myIndex >= 0 ? myIndex + 1 : null;
  const friendsJoined = rows.length - (myIndex >= 0 ? 1 : 0);

  if (friendsJoined <= 0) {
    return (
      <>
        <Placeholder
          Icon={Users}
          title="No friends here yet"
          body={`None of your friends have joined ${leagueName}. Invite them and the head-to-head begins.`}
        />
        <button
          type="button"
          onClick={onInvite}
          className="tap flex w-full items-center justify-center gap-[9px] rounded-2xl border-[1.5px] border-night bg-card py-[15px]"
        >
          <span className="text-[15px] font-extrabold tracking-snug text-ink">Invite friends</span>
        </button>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-[15px] rounded-[22px] border border-black/5 bg-card p-[18px] shadow-tile">
        <div className="text-center">
          <div className="num text-[40px] font-extrabold leading-none tracking-title text-ink">
            {myRank}
            <span className="text-[18px] font-bold text-ink3">{myRank ? ordinalSuffix(myRank) : ''}</span>
          </div>
          <div className="mt-1 text-[11px] font-semibold text-ink2">of {rows.length}</div>
        </div>
        <div className="w-px self-stretch bg-black/[0.07]" />
        <div className="flex-1">
          <div className="text-[15px] font-extrabold tracking-snug text-ink">Among your friends</div>
          <div className="mt-[2px] text-[12px] font-medium text-ink2">
            Ranked by tier in {leagueName}
          </div>
        </div>
      </div>

      <div className="pt-1">
        <span className="text-[13px] font-bold tracking-snug text-ink">By tier · {leagueName}</span>
      </div>

      <div className="flex flex-col gap-1">
        {rows.map((row, i) => (
          <FriendRow key={row.userId} row={row} rank={i + 1} />
        ))}
      </div>

      <button
        type="button"
        onClick={onInvite}
        className="tap flex w-full items-center justify-center gap-[9px] rounded-2xl border-[1.5px] border-night bg-card py-[15px]"
      >
        <span className="text-[15px] font-extrabold tracking-snug text-ink">Invite friends</span>
      </button>
    </>
  );
}

function FriendRow({ row, rank }: { row: FriendStanding; rank: number }) {
  const openProfile = useOpenProfile();
  if (row.isYou) {
    return (
      <button
        type="button"
        onClick={() => openProfile(row.userId, true)}
        className="tap flex w-full items-center gap-3 rounded-2xl bg-night px-[11px] py-[13px] text-left shadow-[0_12px_26px_-12px_rgba(0,0,0,0.5)]"
      >
        <span className="num w-5 text-center text-[15px] font-extrabold text-accent">{rank}</span>
        <div className="grid h-[34px] w-[34px] place-items-center rounded-full bg-white text-[11px] font-extrabold text-ink">
          YOU
        </div>
        <div className="flex-1">
          <div className="text-[14px] font-extrabold tracking-snug text-white">You</div>
          <div className="mt-[2px] flex items-center gap-[5px]">
            <TierDot tierIndex={row.tier} />
            <span className="text-[10px] font-semibold text-[#9A9AA4]">{cap(tierName(row.tier))}</span>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => openProfile(row.userId)}
      className="tap flex w-full items-center gap-3 rounded-[14px] px-[10px] py-[11px] text-left transition-colors active:bg-fill2/70"
    >
      <span className="num w-5 text-center text-[14px] font-extrabold text-ink">{rank}</span>
      <div className="grid h-8 w-8 place-items-center rounded-full border border-black/[0.06] bg-fill2 text-[11px] font-extrabold text-ink3">
        {initials(row.displayName)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-bold tracking-snug text-ink">{row.displayName}</div>
        <div className="mt-[2px] flex items-center gap-[5px]">
          <TierDot tierIndex={row.tier} />
          <span className="text-[10px] font-semibold text-ink2">{cap(tierName(row.tier))}</span>
        </div>
      </div>
    </button>
  );
}
